/**
 * Trading Validation Dashboard Home Page
 * Real-time stats, health monitor, live feed preview
 */

import React, { useEffect, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import '../styles/validation.css';

interface LiveStats {
  win_rate: number;
  daily_return: number;
  drawdown: number;
  active_trades: number;
  total_trades_today: number;
  sharpe_ratio: number;
  profit_factor: number;
  health_status: string;
  system_uptime_pct: number;
}

interface HealthData {
  api: { status: string; latency_ms: number };
  data_feed: { status: string; lag_seconds: number };
  database: { status: string; latency_ms: number };
  capital: { status: string; difference_pct: number };
  overall_status: string;
}

interface Trade {
  timestamp: string;
  type: 'entry' | 'exit';
  symbol: string;
  price: number;
  size: number;
  status: string;
}

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

const TradingValidationHome: React.FC = () => {
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [feed, setFeed] = useState<Trade[]>([]);
  const [dailyTrend, setDailyTrend] = useState<any[]>([]);
  const [enhancements, setEnhancements] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch initial data
    const fetchData = async () => {
      try {
        const [statsRes, healthRes, feedRes, trendRes, enhRes] = await Promise.all([
          fetch(`${API_BASE}/live-stats`),
          fetch(`${API_BASE}/health-checks`),
          fetch(`${API_BASE}/live-feed?limit=5`),
          fetch(`${API_BASE}/daily-trend`),
          fetch(`${API_BASE}/enhancements`),
        ]);

        const statsData = await statsRes.json();
        const healthData = await healthRes.json();
        const feedData = await feedRes.json();
        const trendData = await trendRes.json();
        const enhData = await enhRes.json();

        setStats(statsData.data);
        setHealth(healthData.data);
        setFeed(feedData.data || []);
        setDailyTrend(trendData.data || []);
        setEnhancements(enhData.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();

    // Poll for updates every 1 minute
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="validation-container">
        <div className="loading">Loading validation dashboard...</div>
      </div>
    );
  }

  const getStatusClass = (value: number, target: number, isLow?: boolean) => {
    if (isLow) {
      return value >= target ? 'status-good' : 'status-warning';
    }
    return value >= target ? 'status-good' : 'status-warning';
  };

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>Trading Validation Dashboard</h1>
        <p className="subtitle">Real-time monitoring of 40-day validation period</p>
      </div>

      {/* Stats Tiles */}
      <div className="stats-grid">
        <div className={`stat-tile ${getStatusClass(stats?.win_rate || 0, 0.70)}`}>
          <div className="stat-label">Win Rate</div>
          <div className="stat-value">{((stats?.win_rate || 0) * 100).toFixed(0)}%</div>
          <div className="stat-target">Target: 72%</div>
          <div className="stat-bar">
            <div className="stat-bar-fill" style={{ width: `${Math.min((stats?.win_rate || 0) * 100, 100)}%` }}></div>
          </div>
        </div>

        <div className={`stat-tile ${getStatusClass(stats?.daily_return || 0, 0.045)}`}>
          <div className="stat-label">Daily Return</div>
          <div className="stat-value">+{((stats?.daily_return || 0) * 100).toFixed(1)}%</div>
          <div className="stat-target">Target: +5.2%</div>
          <div className="stat-bar">
            <div className="stat-bar-fill" style={{ width: `${Math.min((stats?.daily_return || 0) * 100, 100)}%` }}></div>
          </div>
        </div>

        <div className={`stat-tile ${getStatusClass(Math.abs(stats?.drawdown || 0), 5, true)}`}>
          <div className="stat-label">Max Drawdown</div>
          <div className="stat-value">{(stats?.drawdown || 0).toFixed(1)}%</div>
          <div className="stat-target">Target: -4.5%</div>
          <div className="stat-bar">
            <div className="stat-bar-fill danger" style={{ width: `${Math.abs(stats?.drawdown || 0)}%` }}></div>
          </div>
        </div>

        <div className={`stat-tile ${getStatusClass(stats?.sharpe_ratio || 0, 2.5)}`}>
          <div className="stat-label">Sharpe Ratio</div>
          <div className="stat-value">{(stats?.sharpe_ratio || 0).toFixed(1)}</div>
          <div className="stat-target">Target: 2.8</div>
          <div className="stat-bar">
            <div className="stat-bar-fill" style={{ width: `${Math.min((stats?.sharpe_ratio || 0) / 4 * 100, 100)}%` }}></div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="content-grid">
        {/* Health Monitor Widget */}
        <div className="widget health-widget">
          <h3>System Health</h3>
          <div className="health-checks">
            <div className={`health-item ${health?.api?.status === 'OK' ? 'ok' : 'warning'}`}>
              <div className="health-indicator"></div>
              <div className="health-content">
                <div className="health-label">Alpaca API</div>
                <div className="health-value">{health?.api?.latency_ms}ms</div>
              </div>
            </div>
            <div className={`health-item ${health?.data_feed?.status === 'OK' ? 'ok' : 'warning'}`}>
              <div className="health-indicator"></div>
              <div className="health-content">
                <div className="health-label">Data Feed</div>
                <div className="health-value">{health?.data_feed?.lag_seconds}s lag</div>
              </div>
            </div>
            <div className={`health-item ${health?.database?.status === 'OK' ? 'ok' : 'warning'}`}>
              <div className="health-indicator"></div>
              <div className="health-content">
                <div className="health-label">Database</div>
                <div className="health-value">{health?.database?.latency_ms}ms</div>
              </div>
            </div>
            <div className={`health-item ${health?.capital?.status === 'OK' ? 'ok' : 'warning'}`}>
              <div className="health-indicator"></div>
              <div className="health-content">
                <div className="health-label">Capital Match</div>
                <div className="health-value">{health?.capital?.difference_pct?.toFixed(2)}%</div>
              </div>
            </div>
          </div>
          <div className={`overall-status ${health?.overall_status === 'HEALTHY' ? 'healthy' : 'warning'}`}>
            Overall: {health?.overall_status}
          </div>
        </div>

        {/* Live Feed Preview */}
        <div className="widget feed-widget">
          <h3>Latest Activity</h3>
          <div className="feed-list">
            {feed.length > 0 ? (
              feed.map((item, idx) => (
                <div key={idx} className={`feed-item ${item.type}`}>
                  <div className="feed-time">{new Date(item.timestamp).toLocaleTimeString()}</div>
                  <div className="feed-details">
                    <span className="feed-type">{item.type.toUpperCase()}</span>
                    <span className="feed-symbol">{item.symbol}</span>
                    <span className="feed-price">${item.price}</span>
                  </div>
                  <div className="feed-status">{item.status}</div>
                </div>
              ))
            ) : (
              <div className="no-data">No recent activity</div>
            )}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* 3-Day Performance */}
        <div className="widget chart-widget">
          <h3>3-Day Performance Trend</h3>
          {dailyTrend.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyTrend.slice(-3)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="return_pct" stroke="#4CAF50" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Active Enhancements */}
        <div className="widget enhancements-widget">
          <h3>Active Enhancements</h3>
          {enhancements && (
            <div className="enhancements-list">
              {Object.entries(enhancements).slice(0, 5).map(([key, value]: any) => (
                <div key={key} className="enhancement-item">
                  <div className="enhancement-number">{key.split('_')[0]}</div>
                  <div className="enhancement-info">
                    <div className="enhancement-name">{value.name}</div>
                    <div className="enhancement-effectiveness">
                      <span className={`effectiveness ${value.effectiveness?.toLowerCase()}`}>
                        {value.effectiveness}
                      </span>
                    </div>
                  </div>
                  <div className="enhancement-status">✓</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="summary-section">
        <h3>Today's Summary</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">Total Trades:</span>
            <span className="value">{stats?.total_trades_today}</span>
          </div>
          <div className="summary-item">
            <span className="label">Active Positions:</span>
            <span className="value">{stats?.active_trades}</span>
          </div>
          <div className="summary-item">
            <span className="label">Profit Factor:</span>
            <span className="value">{stats?.profit_factor.toFixed(1)}</span>
          </div>
          <div className="summary-item">
            <span className="label">System Uptime:</span>
            <span className="value">{stats?.system_uptime_pct.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingValidationHome;
